// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { Ticket, ticketCategorySchema } from '@/ticket/api/types';
import { TicketCategoryDropdown } from '@/ticket/components/ticket-category-dropdown';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { TICKET_CATEGORY_DISPLAY_MAP } from '@/ticket/util/labels';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Chip, useTheme } from '@mui/material';
import { useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ category: ticketCategorySchema });

type FormData = z.infer<typeof formSchema>;

interface Props {
  ticket: Ticket;
}

export function TicketCategoryControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { control, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: props.ticket.category,
    },
  });

  const updateTicket = useUpdateTicket(props.ticket.key, {
    onSuccess: (ticket) => {
      reset({ category: ticket.category });
      editor.close();
    },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (data.category === props.ticket.category) {
      return editor.close();
    }

    updateTicket.mutate({
      category: data.category,
    });
  };

  return (
    <Box>
      <EditablePropertyButton ref={buttonRef} onClick={editor.open}>
        <Chip
          label={TICKET_CATEGORY_DISPLAY_MAP[props.ticket.category]}
          size="small"
        />
      </EditablePropertyButton>
      <SupportSnackbar
        isOpen={updateTicket.isError}
        onClose={updateTicket.reset}
        message="An error occurred while updating the ticket."
      />
      <PopoverForm
        position="left"
        anchorElement={buttonRef.current}
        isOpen={editor.isOpen}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
        width="300px"
      >
        <TicketCategoryDropdown control={control} name={'category'} />
      </PopoverForm>
    </Box>
  );
}
